import type { CancelScheduledDowngradeModalSource } from "../components/shared/UpgradePlan/types";
import { CANCEL_SCHEDULED_DOWNGRADE_SOURCES } from "../components/shared/UpgradePlan/types";
import type { Plan } from "../types";

export const planTiers = Object.freeze({
  basic: "basic",
  professional: "professional",
  enterprise: "enterprise",
  plus: "plus",
  business: "business",
});

export const tiersHierarchy: Record<PlanTiers, number> = Object.freeze({
  basic: 1,
  professional: 2,
  business: 3,
  enterprise: 4,
  plus: 5,
});

export const planIntervals = Object.freeze({
  month: "month",
  year: "year",
  quarter: "quarter",
  biannual: "biannual",
});

export type PlanTiers = keyof typeof planTiers;

export const isAnnual = (interval: any) => interval === planIntervals.year;
export const isMonthly = (interval: any) => interval === planIntervals.month;

export const isPlanBasic = (tier: any) => tier === planTiers.basic;
export const isPlanProfessional = (tier: any) =>
  tier === planTiers.professional;
export const isPlanEnterprise = (tier: any) => tier === planTiers.enterprise;
export const isPlanPlus = (tier: any) => tier === planTiers.plus;
export const isPlanBusiness = (tier: any) => tier === planTiers.business;

export const isDowngrading = (currentTier: PlanTiers, newTier: PlanTiers) =>
  tiersHierarchy[newTier] < tiersHierarchy[currentTier];

export const isUpgrading = (currentTier: PlanTiers, newTier: PlanTiers) =>
  tiersHierarchy[newTier] > tiersHierarchy[currentTier];

export const getNextTier = (currentTier: PlanTiers): PlanTiers | null => {
  const allTierKeys = Object.keys(tiersHierarchy) as PlanTiers[];
  const currentTierIndex = allTierKeys.indexOf(currentTier);
  const isNotLastTier = currentTierIndex < allTierKeys.length - 1;
  const nextTier = allTierKeys[currentTierIndex + 1];

  if (isNotLastTier) {
    return nextTier;
  }

  // if current tier is the highest
  return null;
};

export const planAmountWithCurrencySuffix = (
  amount: number | string,
  currency: string,
) => `${amount} ${currency.toUpperCase()}`;

export const getPreviousTier = (currentTier: PlanTiers) => {
  const allTierKeys = Object.keys(tiersHierarchy);
  const currentTierIndex = allTierKeys.indexOf(currentTier);
  const isNotFirstTier = currentTierIndex > 0;
  const previousTier = allTierKeys[currentTierIndex - 1];

  if (isNotFirstTier) {
    return previousTier;
  }

  return null;
};

export const capitalizeFirstLetter = (str: string) => {
  if (!str) return str;
  return str.charAt(0).toUpperCase() + str.slice(1);
};

export const targetDowngradingPlanName = (
  source: CancelScheduledDowngradeModalSource,
  plan?: Plan,
) => {
  if (!plan) return "";
  if (
    source === CANCEL_SCHEDULED_DOWNGRADE_SOURCES.SCHEDULED_DOWNGRADE_BANNER
  ) {
    return plan.name;
  }
  return capitalizeFirstLetter(plan.tier);
};

export const annualDiscountPercentage = (plan: Plan): string =>
  roundToNearestFive(plan.fetch_discount ?? "0");

export const roundToNearestFive = (percentage: string): string => {
  const numericValue = Math.abs(parseInt(percentage.replace("%", ""), 10));
  const roundedValue = Math.round(numericValue / 5) * 5;

  return `${roundedValue}%`;
};

export const nextPlanTierForWorkflowUpgrade = (tier: any) => {
  if (isPlanBasic(tier) || isPlanProfessional(tier)) {
    return planTiers.business;
  }
  return planTiers.enterprise;
};

export const isNextTierPlus = (currentTier: PlanTiers): boolean => {
  const nextTier = getNextTier(currentTier);
  return nextTier === planTiers.plus;
};
